import { useTranslation } from "react-i18next";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import type { ActivityType } from "@/hooks/useActivityTypes";

interface ActivityFilterSidebarProps {
  typeFilter: string;
  onTypeChange: (type: string) => void;
  onlyJoinable: boolean;
  onOnlyJoinableChange: (v: boolean) => void;
  matchMyLevel?: boolean;
  onMatchMyLevelChange?: (v: boolean) => void;
  activityTypes?: ActivityType[];
}

export default function ActivityFilterSidebar({
  typeFilter,
  onTypeChange,
  onlyJoinable,
  onOnlyJoinableChange,
  matchMyLevel,
  onMatchMyLevelChange,
  activityTypes = [],
}: ActivityFilterSidebarProps) {
  const { t } = useTranslation("activities");

  const hasActiveFilters =
    typeFilter !== "all" || onlyJoinable || (matchMyLevel ?? false);

  const handleClearFilters = () => {
    onTypeChange("all");
    onOnlyJoinableChange(false);
    onMatchMyLevelChange?.(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">{t("filters")}</h2>
        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            className="text-xs text-primary hover:underline"
          >
            {t("clearFilters")}
          </button>
        )}
      </div>

      {/* Activity type filter */}
      <div className="space-y-2">
        <p className="text-xs font-medium text-muted-foreground">{t("activityType")}</p>
        <div className="flex flex-col gap-1">
          <Badge
            variant={typeFilter === "all" ? "default" : "outline"}
            className={cn(
              "cursor-pointer select-none justify-start transition-colors",
              typeFilter === "all" ? "" : "hover:bg-muted"
            )}
            onClick={() => onTypeChange("all")}
          >
            {t("allTypes")}
          </Badge>
          {activityTypes.map((at) => (
            <Badge
              key={at.slug}
              variant={typeFilter === at.slug ? "default" : "outline"}
              className={cn(
                "cursor-pointer select-none justify-start gap-1.5 transition-colors",
                typeFilter === at.slug ? "" : "hover:bg-muted"
              )}
              onClick={() => onTypeChange(at.slug)}
            >
              <span className="text-xs">{at.icon}</span>
              {at.name}
            </Badge>
          ))}
        </div>
      </div>

      {/* Toggles */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Switch
            checked={onlyJoinable}
            onCheckedChange={onOnlyJoinableChange}
            id="sidebar-joinable"
          />
          <Label
            htmlFor="sidebar-joinable"
            className="text-sm text-muted-foreground cursor-pointer"
          >
            {t("onlyAvailableMatches")}
          </Label>
        </div>
        {onMatchMyLevelChange && (
          <div className="flex items-center gap-2">
            <Switch
              checked={matchMyLevel ?? false}
              onCheckedChange={onMatchMyLevelChange}
              id="sidebar-matchLevel"
            />
            <Label
              htmlFor="sidebar-matchLevel"
              className="text-sm text-muted-foreground cursor-pointer"
            >
              {t("matchMyLevel")}
            </Label>
          </div>
        )}
      </div>

      {/* CTA — T2v2: the canonical wizard. The legacy template quick-create
          block died with /create-match (the wizard has no template param);
          templates return with the post-blitz admin wizard (see todo chip). */}
      <Button asChild className="w-full gap-1.5">
        <Link to="/create-activity">
          <Plus className="h-4 w-4" />
          {t("createActivity")}
        </Link>
      </Button>
    </div>
  );
}
